import { useState } from "react";
import { Search, TrendingUp, Mail, CheckCircle, Tag, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "../data/blogPosts";

import { useTranslation } from 'react-i18next';
function slugify(str) {
  return str.toLowerCase().replace(/\s+/g, "-");
}

function buildCategories() {
  const counts = { All: BLOG_POSTS.length };
  BLOG_POSTS.forEach((p) => {
    const cat = p.category || "Other";
    counts[cat] = (counts[cat] || 0) + 1;
  });
  return Object.entries(counts).map(([name, count]) => ({ name, count }));
}

export default function BlogSidebar({ onSearch, onCategoryChange, activeCategory }) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const categories = buildCategories();

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (onSearch) onSearch(val);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <aside className="space-y-8">
      {/* Search */}
      <div className="border border-zinc-900 p-4">
        <div className="flex items-center gap-3 border-b border-zinc-900 pb-3">
          <Search className="w-4 h-4 text-zinc-400" aria-hidden="true" />
          <input
            type="text"
            placeholder={t('blog_sidebar.search_articles')}
            value={searchValue}
            onChange={handleSearch}
            aria-label={t('blog_sidebar.search_articles')}
            className="bg-transparent text-sm text-white placeholder-zinc-600 outline-none w-full font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          />
        </div>
      </div>

      {/* Mobile toggle for filters */}
      <button
        type="button"
        onClick={() => setFiltersOpen(!filtersOpen)}
        className="lg:hidden flex items-center justify-between w-full border border-zinc-900 p-4 text-sm font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
        aria-expanded={filtersOpen}
      >
        <span>{filtersOpen ? "Hide Filters" : "Show Filters"}</span>
        {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Collapsible sections (hidden on mobile when collapsed) */}
      <div className={`${filtersOpen ? "block" : "hidden"} lg:block space-y-8`}>
      {/* Categories */}
      <div className="border border-zinc-900 p-4">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-4">{t('blog_sidebar.categories')}</h3>
        <ul className="space-y-2" role="list">
          {categories.map((cat) => (
            <li key={cat.name}>
              <Link
                to={cat.name === "All" ? "/blog" : `/blog/category/${slugify(cat.name)}`}
                className={`flex items-center justify-between w-full text-sm py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                  activeCategory === cat.name
                    ? "text-amber-500"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <span>{cat.name}</span>
                <span className="font-mono text-[10px] text-zinc-400">{cat.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Popular Posts */}
      <div className="border border-zinc-900 p-4">
        <h3 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-4">
          <TrendingUp className="w-3 h-3" aria-hidden="true" />
          Popular Posts
        </h3>
        <ul className="space-y-4" role="list">
          {BLOG_POSTS.slice(0, 3).map((post) => (
            <li key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                <h4 className="text-sm text-zinc-300 group-hover:text-amber-500 transition-colors leading-snug">
                  {post.title}
                </h4>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 mt-1 block">
                  {post.readTime}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Tags Cloud */}
      <div className="border border-zinc-900 p-4">
        <h3 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-4">
          <Tag className="w-3 h-3" aria-hidden="true" />
          Trending Tags
        </h3>
        <div className="flex flex-wrap gap-2">
          {[...new Set(BLOG_POSTS.flatMap((p) => p.tags || []))].slice(0, 12).map((tag) => (
            <Link
              key={tag}
              to={`/blog/tag/${slugify(tag)}`}
              className="px-2 py-1 border border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-amber-500 hover:border-amber-500/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>

      {/* Newsletter CTA */}
      <div className="border border-amber-500/30 bg-amber-500/5 p-6">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-amber-500" aria-hidden="true" />
          <h3 className="font-display text-sm uppercase text-white">{t('blog_sidebar.newsletter')}</h3>
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed mb-4">{t('blog_sidebar.get_the_latest_ai_agent_insights_delivered_to')}</p>
        {subscribed ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm">
            <CheckCircle className="w-4 h-4" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-wider">{t('blog_sidebar.subscribed')}</span>
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="space-y-3">
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t('blog_sidebar.email_address')}
              className="w-full bg-black/50 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 font-mono outline-none focus:border-amber-500 transition-colors"
            />
            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >{t('blog_sidebar.subscribe')}</button>
          </form>
        )}
      </div>
      </div>
    </aside>
  );
}
